import type { IconProps } from '@shared/icons/types';
import { twMerge } from '@shared/themes';
import { ROUTES } from '@shared/utils';
import { useTranslations } from 'next-intl';
import { Link } from '../Link/Link';
import { LoginButton } from '../LoginButton/index';

type LoginOrSignUpProps = {
  namespace: string;
  icon?: React.ComponentType<IconProps>;
  className?: string;
};

/**
 * A component to render the login cta and sign-up links, usually to guard auth-protected content.
 *
 * @param icon - icon to display
 * @param namespace - translations namespace to infer title, subtitle, login button text, and sign-up button text
 */
function LoginOrSignUp({ namespace, icon, className }: LoginOrSignUpProps) {
  const t = useTranslations(namespace);
  const general = useTranslations('general.login_or_signup');
  const IconComponent = icon as React.ComponentType<IconProps> | undefined;
  const showSubtitle = t.has('subtitle');

  return (
    <div className={twMerge('flex flex-col gap-4 items-center px-4 py-21.5 text-center', className)}>
      {IconComponent && <IconComponent className="size-16 text-foreground" />}
      <div className="flex flex-col gap-2">
        <h4>{t('title')}</h4>
        {showSubtitle ? <p className="paragraph text-foreground-subtle">{t('subtitle')}</p> : null}
      </div>

      <LoginButton variant="accent" data-testid="login-or-signup-login-button">
        {general('login_button')}
      </LoginButton>

      <div className="flex gap-1">
        <span className="text-foreground-subtle">{general('signup_hint')}</span>
        <Link href={ROUTES.REGISTER} data-testid="login-or-signup-signup-link">
          {general('signup_button')}
        </Link>
      </div>
    </div>
  );
}

export { LoginOrSignUp, type LoginOrSignUpProps };
